import { FC } from 'react';

import { Formik } from 'formik';
import { isStrictEqual } from 'remeda';
import { BaseDateTimePicker, BaseInput, BaseSelect, FormBodyContainer } from 'wbs-components-lib';

import { TicketSettingsFormProps } from './types';
import { getTicketTypeSelectItems } from './utils';
import { ticketFormSchema } from './validationSchema';

import { FileSelector } from 'v2/components/fileSystem/fileSelector/FileSelector';
import { RichTextarea } from 'v2/components/richTextarea/RichTextarea';
import { TicketType } from 'v2/constants';

export const TicketSettingsForm: FC<TicketSettingsFormProps> = ({
  isUserService = false,
  initialValues,
  isSubmitting = false,
  submitButtonCaption = 'Create',
  onSubmit,
}) => (
  <Formik initialValues={initialValues} onSubmit={onSubmit} validationSchema={ticketFormSchema} enableReinitialize>
    {({ submitForm, values, handleChange, handleBlur, errors, touched, setFieldValue }) => {
      const {
        description: descriptionValue,
        date: dateValue,
        backgroundImageId: backgroundImageIdValue,
        title: titleValue,
        subtitle: subtitleValue,
        type: typeValue,
        link: linkValue,
        details: detailsValue,
        prizeValue,
      } = values;
      const {
        date: dateError,
        title: titleError,
        subtitle: subtitleError,
        type: typeError,
        link: linkError,
        prizeValue: prizeValueError,
        backgroundImageId: backgroundImageIdError,
      } = errors;
      const {
        type: isTypeTouched,
        title: isTitleTouched,
        subtitle: isSubtitleTouched,
        date: isDateTouched,
        link: isLinkTouched,
        prizeValue: isPrizeValueTouched,
      } = touched;

      const onModalSettingSubmit = async () => {
        await submitForm();
      };

      const isTypeError = isTypeTouched && !!typeError;
      const isTitleError = isTitleTouched && !!titleError;
      const isSubtitleError = isSubtitleTouched && !!subtitleError;
      const isDateError = isDateTouched && !!dateError;
      const isLinkError = isLinkTouched && !!linkError;
      const isPrizeValueError = isPrizeValueTouched && !!prizeValueError;

      const isSubmitButtonDisabled = isStrictEqual(values, initialValues) || isUserService;

      return (
        <FormBodyContainer
          isSubmitting={isSubmitting}
          onSubmit={onModalSettingSubmit}
          submitButtonCaption={submitButtonCaption}
          isSubmitButtonDisabled={isSubmitButtonDisabled}
        >
          <BaseSelect
            // @ts-ignore
            id="type"
            name="type"
            errorText={typeError}
            isError={isTypeError}
            onBlur={handleBlur}
            items={getTicketTypeSelectItems()}
            handleChange={(value: string) => setFieldValue('type', value)}
            value={typeValue}
            caption="Type Type"
            label="Select type"
          />
          <BaseInput
            helperText={isTitleError && titleError}
            label="Title"
            id="title"
            name="title"
            value={titleValue}
            onBlur={handleBlur}
            onChange={handleChange}
            error={isTitleError}
          />
          <BaseInput
            helperText={isSubtitleError && subtitleError}
            label="Subtitle"
            id="subtitle"
            name="subtitle"
            value={subtitleValue}
            onBlur={handleBlur}
            onChange={handleChange}
            error={isSubtitleError}
          />
          <RichTextarea
            label="Description"
            value={descriptionValue}
            onChange={(value: string) => setFieldValue('description', value)}
          />
          {typeValue === TicketType.CONTEST && (
            <>
              <BaseInput
                helperText={isLinkError && linkError}
                error={isLinkError}
                id="link"
                name="link"
                label="Enter link"
                value={linkValue}
                onBlur={handleBlur}
                onChange={handleChange}
              />
              <BaseInput
                helperText={isPrizeValueError && prizeValueError}
                error={isPrizeValueError}
                id="prizeValue"
                name="prizeValue"
                label="Enter prize value"
                value={prizeValue}
                onBlur={handleBlur}
                onChange={handleChange}
              />
              <RichTextarea
                label="Details"
                value={detailsValue}
                onChange={(value: string) => setFieldValue('details', value)}
              />
            </>
          )}
          <BaseDateTimePicker
            id="date"
            name="date"
            errorMessage={isDateError ? (dateError as string) : undefined}
            onBlur={handleBlur}
            caption="Date"
            value={dateValue}
            onChange={(value) => setFieldValue('date', value)}
          />
          <FileSelector
            label="Background Image"
            value={backgroundImageIdValue ?? ''}
            error={backgroundImageIdError}
            onSelect={(value) => setFieldValue('backgroundImageId', value)}
            required={typeValue === TicketType.CONTEST}
          />
        </FormBodyContainer>
      );
    }}
  </Formik>
);
